import {
  Action,
  DropdownActionMenuItem,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';

import {
  useDeleteWorkshopInterestMutation,
  useGetWorkshopInterestQuery,
  useOpenWorkshopInterestMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {interestRoutes, testIds} from '@dms/routes';
import {handleApiError, useWorkshopInterestEditPermissions} from '@dms/shared';

import {buildArray, useNavigate} from 'shared/web';

import {WorkshopInterestEndCaseDialog} from '../components/WorkshopInterestEndCaseDialog';

export function useWorkshopInterestActions(interestId: string) {
  const navigate = useNavigate();

  const {data: workshopInterest} = useGetWorkshopInterestQuery({id: interestId});

  const [deleteWorkshopInterest] = useDeleteWorkshopInterestMutation();
  const [openWorkshopInterest, {isLoading: isOpenLoading}] = useOpenWorkshopInterestMutation();

  const {hasOpenPermission, hasEndPermission, hasDeletePermission} =
    useWorkshopInterestEditPermissions({interestId});

  const canDelete =
    (workshopInterest?.availableActions?.includes('DELETE') ?? false) && hasDeletePermission;
  const isOpenAvailable =
    workshopInterest?.state === 'NEW' &&
    (workshopInterest?.availableActions?.includes('OPEN') ?? false);
  const onEndClick = () =>
    openDialog(
      <WorkshopInterestEndCaseDialog
        data-testid={testIds.workshop.interestDetail('endCaseDialog')}
        interestId={interestId}
      />,
      {
        'data-testid': testIds.workshop.interestDetail('endCaseDialog'),
        title: i18n.t('entity.workshopInterest.labels.endInterestCase'),
        scrollBehavior: 'outside',
      }
    );

  const dropdownMenuItems = buildArray<DropdownActionMenuItem>().when(canDelete, {
    label: i18n.t('general.actions.delete'),
    onClick: () =>
      openDeleteDialog({
        onConfirm: () =>
          deleteWorkshopInterest({id: interestId})
            .unwrap()
            .then(() => {
              showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
              navigate(interestRoutes.workshopOverview);
            })
            .catch(handleApiError),
      }),
  });

  return buildArray<Action>()
    .when(workshopInterest?.state === 'NEW' && dropdownMenuItems.length > 0, {
      type: 'dropdown-iconButton',
      priority: 'secondary',
      menuItems: dropdownMenuItems,
      'data-testid': testIds.workshop.interestDetail('headerActions-submenu'),
    })
    .when(workshopInterest?.state === 'OPEN', {
      type: 'button',
      variant: 'secondary',
      title: i18n.t('general.actions.end'),
      isDisabled: !hasEndPermission,
      tooltip: hasEndPermission ? undefined : i18n.t('general.labels.noPermission'),
      onClick: onEndClick,
      'data-testid': testIds.workshop.interestDetail('headerActions-end'),
    })
    .when(isOpenAvailable, {
      type: 'button',
      variant: 'primary',
      title: i18n.t('general.actions.open'),
      isLoading: isOpenLoading,
      isDisabled: !hasOpenPermission || isOpenLoading,
      tooltip: hasOpenPermission ? undefined : i18n.t('general.labels.noPermission'),
      onClick: () => openWorkshopInterest({id: interestId}).unwrap().catch(handleApiError),
      'data-testid': testIds.workshop.interestDetail('headerActions-open'),
    });
}
